import { useEffect, useRef, useState } from 'react';
import * as pdfjsLib from 'pdfjs-dist';
import pdfWorker from 'pdfjs-dist/build/pdf.worker.min.mjs?url';

pdfjsLib.GlobalWorkerOptions.workerSrc = pdfWorker;

/**
 * Universal file renderer. Points at a server "/rendered" URL that returns
 * either native bytes (image/video/audio/text/plain-pdf) or a LibreOffice-
 * converted PDF for office docs. PDFs render via PDF.js into <canvas> (crisp,
 * consistent across browsers + mobile). Renders identically to the iOS PDFKit
 * viewer since it's the same source PDF.
 *
 * Props: { src: string (rendered URL), filename?: string }
 */
export default function FileRenderer({ src, filename = 'file' }) {
  const [state, setState] = useState({ kind: 'loading' });
  const pdfContainer = useRef(null);

  // Fetch + classify by response Content-Type.
  useEffect(() => {
    let cancelled = false;
    let objectUrl = null;
    (async () => {
      setState({ kind: 'loading' });
      try {
        const res = await fetch(src, { credentials: 'include' });
        if (!res.ok) {
          if (res.status === 415 || res.status === 413) { setState({ kind: 'unsupported' }); return; }
          throw new Error(`HTTP ${res.status}`);
        }
        const ct = (res.headers.get('Content-Type') || '').toLowerCase();
        const buf = await res.arrayBuffer();
        if (cancelled) return;
        if (ct.includes('pdf')) {
          setState({ kind: 'pdf', data: buf });
        } else if (ct.startsWith('image/')) {
          objectUrl = URL.createObjectURL(new Blob([buf], { type: ct }));
          setState({ kind: 'image', url: objectUrl });
        } else if (ct.startsWith('video/')) {
          objectUrl = URL.createObjectURL(new Blob([buf], { type: ct }));
          setState({ kind: 'video', url: objectUrl });
        } else if (ct.startsWith('audio/')) {
          objectUrl = URL.createObjectURL(new Blob([buf], { type: ct }));
          setState({ kind: 'audio', url: objectUrl });
        } else if (ct.startsWith('text/')) {
          setState({ kind: 'text', text: new TextDecoder().decode(buf) });
        } else {
          objectUrl = URL.createObjectURL(new Blob([buf], { type: ct || 'application/octet-stream' }));
          setState({ kind: 'download', url: objectUrl });
        }
      } catch (e) {
        if (!cancelled) setState({ kind: 'error', message: e.message });
      }
    })();
    return () => { cancelled = true; if (objectUrl) URL.revokeObjectURL(objectUrl); };
  }, [src]);

  // Render all PDF pages to canvases.
  useEffect(() => {
    if (state.kind !== 'pdf' || !pdfContainer.current) return;
    let cancelled = false;
    const container = pdfContainer.current;
    (async () => {
      container.innerHTML = '';
      // Copy the buffer — pdf.js transfers/detaches it.
      const pdf = await pdfjsLib.getDocument({ data: state.data.slice(0) }).promise;
      const scale = Math.min(2, (window.devicePixelRatio || 1) * 1.3);
      for (let n = 1; n <= pdf.numPages; n++) {
        if (cancelled) return;
        const page = await pdf.getPage(n);
        const viewport = page.getViewport({ scale });
        const canvas = document.createElement('canvas');
        canvas.width = viewport.width;
        canvas.height = viewport.height;
        canvas.style.width = '100%';
        canvas.style.height = 'auto';
        canvas.style.marginBottom = '10px';
        canvas.style.boxShadow = '0 1px 6px rgba(0,0,0,0.15)';
        canvas.style.borderRadius = '4px';
        container.appendChild(canvas);
        await page.render({ canvasContext: canvas.getContext('2d'), viewport }).promise;
      }
    })().catch((e) => { if (!cancelled) setState({ kind: 'error', message: e.message }); });
    return () => { cancelled = true; };
  }, [state]);

  switch (state.kind) {
    case 'loading':
      return (
        <div className="flex flex-col items-center justify-center gap-5 py-24 text-gray-500">
          {/* Pulsing document skeleton — a page filling in line by line */}
          <div className="w-16 h-20 bg-white border border-gray-200 rounded-md shadow-sm p-2.5 flex flex-col gap-[5px]">
            <div className="h-1.5 w-3/4 bg-gray-200 rounded animate-pulse" />
            <div className="h-1.5 w-full bg-gray-200 rounded animate-pulse [animation-delay:150ms]" />
            <div className="h-1.5 w-2/3 bg-gray-200 rounded animate-pulse [animation-delay:300ms]" />
            <div className="h-1.5 w-5/6 bg-gray-200 rounded animate-pulse [animation-delay:450ms]" />
            <div className="h-1.5 w-1/2 bg-gray-200 rounded animate-pulse [animation-delay:600ms]" />
          </div>
          <div className="flex items-center gap-2">
            <svg className="w-4 h-4 animate-spin text-blue-500" viewBox="0 0 24 24" fill="none">
              <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" className="opacity-25" />
              <path d="M4 12a8 8 0 018-8" stroke="currentColor" strokeWidth="4" strokeLinecap="round" />
            </svg>
            <span className="text-sm font-medium">Rendering…</span>
          </div>
        </div>
      );
    case 'pdf':
      return <div ref={pdfContainer} className="overflow-auto bg-gray-100 p-2 rounded" style={{ maxHeight: '82vh' }} />;
    case 'image':
      return <div className="overflow-auto text-center" style={{ maxHeight: '82vh' }}><img src={state.url} alt={filename} className="max-w-full inline-block" /></div>;
    case 'video':
      return <video src={state.url} controls className="max-w-full mx-auto" style={{ maxHeight: '82vh' }} />;
    case 'audio':
      return <div className="p-8"><audio src={state.url} controls className="w-full" /></div>;
    case 'text':
      return <pre className="p-4 text-sm whitespace-pre-wrap font-mono overflow-auto bg-gray-50 rounded" style={{ maxHeight: '82vh' }}>{state.text}</pre>;
    case 'unsupported':
    case 'download':
      return (
        <div className="p-10 text-center">
          <a href={state.url || src} download={filename} className="text-blue-600 underline font-medium">Download {filename}</a>
          <p className="text-gray-500 text-sm mt-2">No inline preview available for this file type.</p>
        </div>
      );
    case 'error':
      return <div className="p-10 text-center text-red-500">Failed to render: {state.message}</div>;
    default:
      return null;
  }
}
